"use client";

import { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer({
  scrolled
}: {
  scrolled: boolean;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-24 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-28 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={`group relative bg-transparent hover:bg-transparent focus:bg-transparent px-0 py-1 text-[13px] font-default font-light tracking-[0.1em] uppercase transition-colors duration-300 ${scrolled ? "text-foreground hover:text-foreground" : "text-foreground hover:text-foreground"}`}>
                <span className="relative inline-block">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-[400ms] ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8 py-12">
        <div className="h-10 w-3/4 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-10 w-3/4 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-10 w-1/2 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-8 px-8 pt-16 pb-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="group flex items-baseline gap-5 text-foreground">
          <span className="font-mono text-xs font-light tracking-[0.15em] text-primary pt-2">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="font-default text-3xl font-light leading-tight tracking-tight transition-colors duration-300 group-hover:text-primary">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="173" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-500 ease-out bg-background ${scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex h-20 items-center justify-between lg:h-24">
          {}
          <Link to="/" className="flex items-center transition-opacity duration-300 hover:opacity-80" aria-label="Sparkle & Shine Maids Home">
            <WvcLogo className="h-7 w-auto" />
          </Link>

          {}
          <div className="hidden lg:flex lg:flex-1 lg:justify-center">
            <MenuProvider menu_id="31">
              <DesktopMenuConsumer scrolled={scrolled} />
            </MenuProvider>
          </div>

          {}
          <div className="hidden lg:flex lg:flex-col lg:items-end lg:gap-2">
            <Link to="tel:+15559876543" className={`flex items-center gap-1.5 text-[11px] font-default font-light tracking-[0.15em] uppercase transition-colors duration-300 text-foreground hover:text-primary`}>
              <Phone className="h-3 w-3" />
              <span>+1 555-987-6543</span>
            </Link>
            <Button asChild variant="outline" className={`h-9 rounded-lg border border-primary bg-primary px-5 text-[11px] font-default font-medium tracking-[0.18em] uppercase shadow-none transition-all duration-300 text-white hover:bg-primary/90`}>
              <Link to="/GetInTouchPage">Get Quote</Link>
            </Button>
          </div>

          {}
          <div className="flex lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className={`h-10 w-10 bg-transparent hover:bg-transparent text-foreground`} aria-label="Open menu">
                  <Menu className="h-6 w-6" strokeWidth={1.25} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-0 flex flex-col">
                <div className="flex items-center justify-between px-8 pt-8">
                  <WvcLogo className="h-7 w-auto" />
                </div>

                <div className="flex-1 overflow-y-auto">
                  <MenuProvider menu_id="31">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>

                <div className="border-t border-border px-8 py-6 flex flex-col gap-4">
                  <Link to="tel:+15559876543" className="flex items-center gap-2 text-xs font-default font-light tracking-[0.15em] uppercase text-foreground hover:text-primary transition-colors">
                    <Phone className="h-3.5 w-3.5" />
                    <span>+1 555-987-6543</span>
                  </Link>
                  <Button asChild onClick={() => setMobileOpen(false)} className="h-12 w-full rounded-lg bg-primary text-white text-xs font-default font-medium tracking-[0.2em] uppercase hover:bg-primary/90 shadow-sm">
                    <Link to="/GetInTouchPage">Get Instant Quote</Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}